// DM-list action handlers + memoized DmActions object, mirroring the
// `useChannelActions` pattern used by ChannelItem.memo (see
// apps/desktop/src/components/layout/sidebar/use-channel-actions.ts).
//
// `dmActionsCtxRef` is load-bearing: every DM/Member/GroupDM row is
// wrapped in React.memo, and Sidebar re-renders frequently (Zero
// status flips). If the `actions` prop identity changed each render,
// every row would re-render. Funnel the live state through a ref so
// the actions object stays referentially stable forever — the rows'
// memo bails out cleanly.

import { useMemo, useRef } from "react";
import { useZero } from "@rocicorp/zero/react";
import { mutators } from "@/lib/mutators";
import { useOpenDM } from "@/hooks/use-open-dm";

export interface DmActions {
  /** Open the given DM/group-DM channel by id (used by row click). */
  selectChannel: (channelId: string) => void;
  /** Mark the membership hidden (dropdown "Hide conversation" action). */
  hideChannel: (channelId: string) => void;
  /** Open an existing DM with this user, or create one if none exists. */
  openForUser: (userId: string) => void;
}

interface UseDmActionsArgs {
  onSelectChannel: (id: string | null) => void;
  selectedChannel: string | null;
  /** Map of channelId → membership record for the current user. */
  membershipByChannelId: Map<string, { id: string }>;
  /** Map of userId → existing DM channelId, when one exists. */
  dmByUserId: Map<string, string>;
}

export function useDmActions({
  onSelectChannel,
  selectedChannel,
  membershipByChannelId,
  dmByUserId,
}: UseDmActionsArgs): DmActions {
  const zero = useZero();
  const openDm = useOpenDM(onSelectChannel);

  // Live context — refreshed each render. The actions object reads
  // `.current` at call time so it always sees the latest state.
  const ctxRef = useRef({
    onSelectChannel,
    selectedChannel,
    membershipByChannelId,
    dmByUserId,
    zero,
    openDm,
  });
  // eslint-disable-next-line react-hooks/refs
  ctxRef.current = {
    onSelectChannel,
    selectedChannel,
    membershipByChannelId,
    dmByUserId,
    zero,
    openDm,
  };

  // Empty deps — the returned object is referentially stable for the
  // hook's lifetime. Each action closes over `ctxRef`, not over the
  // current snapshot of caller state.
  return useMemo<DmActions>(
    () => ({
      selectChannel: (channelId) => {
        ctxRef.current.onSelectChannel(channelId);
      },
      hideChannel: (channelId) => {
        const ctx = ctxRef.current;
        const membership = ctx.membershipByChannelId.get(channelId);
        if (!membership) return;
        // Clear selection first to avoid racing with the auto-unhide effect.
        if (ctx.selectedChannel === channelId) {
          ctx.onSelectChannel(null);
        }
        void ctx.zero.mutate(
          mutators.channel_members.update({ id: membership.id, hidden: true }),
        );
        // Hidden DMs resurface in the sidebar while unread (the
        // `!hidden || isUnread` rule in use-dm-list-data), so hiding an
        // *unread* conversation would be a visible no-op. Marking it read
        // clears it from the sidebar AND the inbox immediately; a new message
        // re-flags it unread and it pops back. "Hide" == "dismiss until
        // relevant again."
        void ctx.zero.mutate(
          mutators.actions.markChannelRead({ membershipId: membership.id }),
        );
      },
      openForUser: (userId) => {
        void ctxRef.current.openDm(userId);
      },
    }),
    [],
  );
}
